import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getApiError } from "../../services/courseApi";
import {
  addOrganizationGuru,
  getOrganization,
  listAllGurus,
  listOrganizationCourses,
  listOrganizationGurus,
  removeOrganizationGuru,
  updateOrganization,
} from "../../services/organizationApi";
import { availableGuruOptions, guruName } from "./organizationUtils";
import "../course/course.css";
import "./organization.css";

function formatMoney(payment) {
  if (!payment) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: payment.currency || "INR",
    maximumFractionDigits: 0,
  }).format(payment.amount || 0);
}

export default function OrganizationDetail() {
  const { organizationId } = useParams();
  const [organization, setOrganization] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [allMemberships, setAllMemberships] = useState([]);
  const [courses, setCourses] = useState([]);
  const [allGurus, setAllGurus] = useState([]);
  const [membershipStatus, setMembershipStatus] = useState("");
  const [musicFieldId, setMusicFieldId] = useState("");
  const [selectedGuruId, setSelectedGuruId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function loadOverview() {
    const [organizationData, courseData, guruUsers, membershipData] = await Promise.all([
      getOrganization(organizationId),
      listOrganizationCourses(organizationId),
      listAllGurus(),
      listOrganizationGurus(organizationId),
    ]);
    setOrganization(organizationData);
    setCourses(Array.isArray(courseData) ? courseData : []);
    setAllGurus(Array.isArray(guruUsers) ? guruUsers : []);
    setAllMemberships(Array.isArray(membershipData) ? membershipData : []);
  }

  async function loadMemberships() {
    const params = {};
    if (membershipStatus) params.status = membershipStatus;
    if (musicFieldId) params.music_field_id = musicFieldId;
    const data = await listOrganizationGurus(organizationId, params);
    setMemberships(Array.isArray(data) ? data : []);
  }

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        await Promise.all([loadOverview(), loadMemberships()]);
      } catch (error) {
        toast.error(getApiError(error, "Could not load organization"));
      } finally {
        setLoading(false);
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  useEffect(() => {
    if (loading) return;
    loadMemberships().catch((error) =>
      toast.error(getApiError(error, "Could not filter organization gurus"))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipStatus, musicFieldId]);

  const guruOptions = useMemo(
    () => availableGuruOptions(allGurus, allMemberships),
    [allGurus, allMemberships]
  );

  const selectedGuru = guruOptions.find(
    (guru) => Number(guru.id) === Number(selectedGuruId)
  );

  const musicFields = useMemo(() => {
    const values = new Map();
    allGurus.forEach((guru) => {
      if (guru.music_field_id) {
        values.set(guru.music_field_id, guru.music_field_name || `Field #${guru.music_field_id}`);
      }
    });
    allMemberships.forEach((membership) => {
      if (membership.music_field?.id) {
        values.set(membership.music_field.id, membership.music_field.name);
      }
    });
    return Array.from(values, ([id, name]) => ({ id, name }));
  }, [allGurus, allMemberships]);

  async function addGuru() {
    if (!selectedGuruId) return;
    setSaving(true);
    try {
      await addOrganizationGuru(organizationId, selectedGuruId);
      toast.success(selectedGuru?.action === "Reactivate" ? "Guru membership reactivated" : "Guru added to organization");
      setSelectedGuruId("");
      await Promise.all([loadOverview(), loadMemberships()]);
    } catch (error) {
      toast.error(getApiError(error, "Could not add guru"));
    } finally {
      setSaving(false);
    }
  }

  async function removeGuru(guru) {
    if (!window.confirm(
      `Deactivate ${guruName(guru)}’s organization membership? Personal students and payments will not be changed.`
    )) return;
    setSaving(true);
    try {
      await removeOrganizationGuru(organizationId, guru.guru_id);
      toast.success("Guru membership deactivated");
      await Promise.all([loadOverview(), loadMemberships()]);
    } catch (error) {
      toast.error(getApiError(error, "Could not remove guru"));
    } finally {
      setSaving(false);
    }
  }

  async function toggleOrganizationStatus() {
    const nextStatus = organization.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    if (!window.confirm(`${nextStatus === "ACTIVE" ? "Activate" : "Deactivate"} this organization?`)) return;
    setSaving(true);
    try {
      const updated = await updateOrganization(organizationId, { status: nextStatus });
      setOrganization(updated);
      toast.success(`Organization ${nextStatus === "ACTIVE" ? "activated" : "deactivated"}`);
    } catch (error) {
      toast.error(getApiError(error, "Could not update organization"));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="course-state"><div className="spinner-border text-primary" /></div>;
  }

  if (!organization) {
    return <div className="course-state"><h1>Organization not found</h1><Link className="btn btn-primary" to="/organizations">Back</Link></div>;
  }

  return (
    <main className="course-page">
      <Link className="course-back-link" to="/organizations">
        <i className="bi bi-arrow-left me-2" />Back to organizations
      </Link>
      <div className="course-page-header mt-3">
        <div>
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className={`organization-status ${organization.status?.toLowerCase()}`}>
              {organization.status}
            </span>
            <span className="text-muted small">Organization #{organization.organization_id}</span>
          </div>
          <h1>{organization.name}</h1>
          <p className="text-muted mb-0">Organization course provider and teaching membership.</p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button
            className={`btn ${organization.status === "ACTIVE" ? "btn-outline-danger" : "btn-outline-success"}`}
            disabled={saving}
            onClick={toggleOrganizationStatus}
          >
            {organization.status === "ACTIVE" ? "Deactivate" : "Activate"}
          </button>
          <Link className="btn btn-primary" to={`/organizations/${organizationId}/edit`}>
            <i className="bi bi-pencil me-2" />Edit organization
          </Link>
        </div>
      </div>

      <section className="organization-summary-grid">
        <div><span>Active gurus</span><strong>{organization.active_guru_count || 0}</strong></div>
        <div><span>Organization courses</span><strong>{organization.course_count || 0}</strong></div>
        <div><span>Created</span><strong>{organization.created_at ? new Date(organization.created_at).toLocaleDateString("en-IN") : "—"}</strong></div>
      </section>

      <section className="course-panel">
        <div className="course-section-heading">
          <div>
            <h2>Organization gurus</h2>
            <p className="text-muted mb-0">
              Membership grants organization teaching eligibility; personal work remains separate.
            </p>
          </div>
        </div>

        {organization.status === "ACTIVE" ? (
          <div className="organization-add-guru">
            <select className="form-select" value={selectedGuruId} onChange={(event) => setSelectedGuruId(event.target.value)}>
              <option value="">Select an existing guru</option>
              {guruOptions.map((guru) => (
                <option key={guru.id} value={guru.id}>
                  {guru.label} · {guru.action}
                </option>
              ))}
            </select>
            <button className="btn btn-primary" disabled={!selectedGuruId || saving} onClick={addGuru}>
              {selectedGuru?.action === "Reactivate" ? "Reactivate guru" : "Add guru"}
            </button>
          </div>
        ) : (
          <div className="alert alert-light border mt-3 mb-0">
            Activate the organization before adding gurus.
          </div>
        )}

        <div className="organization-member-filters">
          <select className="form-select" value={membershipStatus} onChange={(event) => setMembershipStatus(event.target.value)}>
            <option value="">All memberships</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
          <select className="form-select" value={musicFieldId} onChange={(event) => setMusicFieldId(event.target.value)}>
            <option value="">All music fields</option>
            {musicFields.map((field) => <option key={field.id} value={field.id}>{field.name}</option>)}
          </select>
        </div>

        {memberships.length === 0 ? (
          <div className="organization-empty-inline">No guru memberships match these filters.</div>
        ) : (
          <div className="table-responsive mt-3">
            <table className="table course-table align-middle mb-0">
              <thead><tr><th>Guru</th><th>Music field</th><th>Membership</th><th>Joined</th><th className="text-end">Action</th></tr></thead>
              <tbody>
                {memberships.map((guru) => (
                  <tr key={guru.guru_id}>
                    <td><strong>{guruName(guru)}</strong><div className="small text-muted">{guru.email || "—"}</div></td>
                    <td>{guru.music_field?.name || "—"}</td>
                    <td><span className={`organization-status ${guru.membership_status?.toLowerCase()}`}>{guru.membership_status}</span></td>
                    <td>{guru.joined_at ? new Date(guru.joined_at).toLocaleDateString("en-IN") : "—"}</td>
                    <td className="text-end">
                      {guru.membership_status === "ACTIVE" ? (
                        <button className="btn btn-sm btn-outline-danger" disabled={saving} onClick={() => removeGuru(guru)}>Deactivate</button>
                      ) : (
                        <button className="btn btn-sm btn-outline-success" disabled={saving} onClick={async () => {
                          setSelectedGuruId(guru.guru_id);
                          setSaving(true);
                          try {
                            await addOrganizationGuru(organizationId, guru.guru_id);
                            toast.success("Guru membership reactivated");
                            setSelectedGuruId("");
                            await Promise.all([loadOverview(), loadMemberships()]);
                          } catch (error) {
                            toast.error(getApiError(error, "Could not reactivate guru"));
                          } finally {
                            setSaving(false);
                          }
                        }}>Reactivate</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="course-panel">
        <div className="course-section-heading">
          <div><h2>Organization courses</h2><p className="text-muted mb-0">Only organization-owned courses are shown here.</p></div>
          <Link className="btn btn-outline-primary" to="/courses/new">Create course</Link>
        </div>
        {courses.length === 0 ? (
          <div className="organization-empty-inline">No courses belong to this organization.</div>
        ) : (
          <div className="table-responsive mt-3">
            <table className="table course-table align-middle mb-0">
              <thead><tr><th>Course</th><th>Type</th><th>Price</th><th>Sessions</th><th>Status</th><th className="text-end">Action</th></tr></thead>
              <tbody>
                {courses.map((course) => (
                  <tr key={course.course_id}>
                    <td><Link className="course-title-link" to={`/courses/${course.course_id}`}>{course.title}</Link><div className="small text-muted">{course.music_field?.name}</div></td>
                    <td>{course.type === "ONE_OFF" ? "One-off" : "Graded"}</td>
                    <td>{formatMoney(course.payment)}</td>
                    <td>{course.session_count}</td>
                    <td><span className={`course-status ${course.status?.toLowerCase()}`}>{course.status}</span></td>
                    <td className="text-end"><Link className="btn btn-sm btn-outline-primary" to={`/courses/${course.course_id}`}>View</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
